import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface ExitSlipEarnedSkillsState {
  // Skills ("SkillNode:<n>") the student has just earned by passing the last exit ticket standing
  // between them and it — the same finish that turns the collapsed banner into its skill-earned
  // state. Held only until the server reports the new skill state, which a reload then reads.
  //
  // Distinct from exitSlipUnlockedSkillsSlice, which is about a card being reachable because its
  // ticket was *sat*. This one is about the skill being *won*, and is what makes the card read as
  // Inferred before the query catches up.
  skillIds: string[];
}

const initialState: ExitSlipEarnedSkillsState = { skillIds: [] };

export const exitSlipEarnedSkillsSlice = createSlice({
  name: 'exitSlipEarnedSkills',
  initialState,
  reducers: {
    setExitSlipEarnedSkillIds: (state: ExitSlipEarnedSkillsState, action: PayloadAction<string[]>) => {
      // Same ids, same order: keep the existing array, so the unit path does not rebuild for nothing
      // (see the same guard on exitSlipUnlockedSkillsSlice).
      const unchanged =
        state.skillIds.length === action.payload.length &&
        state.skillIds.every((skillId, index) => skillId === action.payload[index]);
      if (unchanged) {
        return;
      }
      state.skillIds = action.payload;
    },
  },
});

/**
 * Whether this one skill has just been earned. Returns a boolean rather than the array so a card
 * re-renders only when its own answer changes — handing back the array would give every card a new
 * reference on each store change.
 */
export const selectSkillEarnedViaExitSlip =
  (skillId: string | undefined) =>
  (state: RootState): boolean =>
    !!skillId && state.exitSlipEarnedSkills.skillIds.includes(skillId);

export const { setExitSlipEarnedSkillIds } = exitSlipEarnedSkillsSlice.actions;

export default exitSlipEarnedSkillsSlice.reducer;
